'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import type { ConfiguratorState } from './lib/configurator-math';
import { getProductBendLines, getSelectedOptions } from './lib/configurator-math';
import { resolvePartGeometry, type GeometryPath } from './lib/part-geometry';

type OrbitState = {
  yaw: number;
  pitch: number;
  distance: number;
};

type ModelOrientation = {
  x: number;
  y: number;
  z: number;
  w: number;
};

type ModelDisplayState = {
  yawDeg: number;
  pitchDeg: number;
};

type ViewTarget = {
  x: number;
  y: number;
  z: number;
};

type PickResult = {
  hit: boolean;
  point?: ViewTarget;
};

type DragState = {
  x: number;
  y: number;
  mode: 'model' | 'camera' | 'pan';
  orbitYaw: number;
  orbitPitch: number;
  target: ViewTarget;
};

type ViewPreset = 'front' | 'vertical' | 'horizontal';

type RendererApi = {
  dispose: () => void;
  zoom: (factor: number) => void;
  setPreset: (preset: ViewPreset) => void;
  rotateModelByPointer: (fromX: number, fromY: number, toX: number, toY: number) => void;
  orbitCamera: (yaw: number, pitch: number) => void;
  panView: (deltaX: number, deltaY: number, startTarget: ViewTarget) => void;
  pickObject: (clientX: number, clientY: number) => PickResult;
  reset: () => void;
};

type ThreeModule = typeof import('three');
type ProductBendLine = ReturnType<typeof getProductBendLines>[number];

type MeshVertex = {
  x: number;
  y: number;
  z: number;
};

type MeshPolygon = {
  points: MeshVertex[];
  materialIndex: number;
};

type BendMeshDebugLine = ProductBendLine & {
  axisSizeMm: number;
  centeredCoordMm: number;
};

type MeshTriangleDebug = {
  triangleIndex: number;
  materialIndex: number;
  areaMm2: number;
  edgeLengthsMm: number[];
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    minZ: number;
    maxZ: number;
  };
  crossedBendLines: string[];
};

type MeshDebugSummary = {
  label: string;
  indexed: boolean;
  vertexCount: number;
  triangleCount: number;
  groupCount: number;
  materialGroups: Array<{ materialIndex: number | undefined; start: number; count: number }>;
  bounds: {
    minX: number | null;
    maxX: number | null;
    minY: number | null;
    maxY: number | null;
    minZ: number | null;
    maxZ: number | null;
  };
  degenerateTriangleCount: number;
  skinnyTriangleCount: number;
  bendCrossingTriangleCount: number;
  longestEdges: MeshTriangleDebug[];
  bendCrossingTriangles: MeshTriangleDebug[];
};

const initialOrbit: OrbitState = {
  yaw: -0.72,
  pitch: 0.48,
  distance: 860,
};

const initialModelOrientation: ModelOrientation = {
  x: 1,
  y: 0,
  z: 0,
  w: 0,
};

const initialModelDisplayState: ModelDisplayState = {
  yawDeg: 0,
  pitchDeg: 0,
};

const initialTarget: ViewTarget = {
  x: 0,
  y: 0,
  z: 0,
};

const MODEL_ROTATION_TRACKBALL_SENSITIVITY = 1.05;
const MODEL_ROTATION_MAX_STEP = 0.11;
const CAMERA_ORBIT_HORIZONTAL_SENSITIVITY = 560;
const CAMERA_ORBIT_VERTICAL_SENSITIVITY = 580;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function hexToRgb(hex: string) {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : normalized,
    16,
  );
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function geometryPathToThreePath(
  THREE: ThreeModule,
  path: GeometryPath,
  widthMm: number,
  lengthMm: number,
) {
  const threePath = new THREE.Path();
  const [first, ...rest] = path.points;

  if (!first) return threePath;

  threePath.moveTo(first.xMm - widthMm / 2, first.yMm - lengthMm / 2);
  rest.forEach(point => {
    threePath.lineTo(point.xMm - widthMm / 2, point.yMm - lengthMm / 2);
  });

  if (path.closed) {
    threePath.closePath();
  }

  return threePath;
}

function getBendLineCoord(
  line: ReturnType<typeof getProductBendLines>[number],
  config: ConfiguratorState,
) {
  const axisSize = line.orientation === 'vertical' ? config.widthMm : config.lengthMm;
  return line.positionMm - axisSize / 2;
}

function getVertexAxisCoord(
  vertex: MeshVertex,
  line: ReturnType<typeof getProductBendLines>[number],
) {
  return line.orientation === 'vertical' ? vertex.x : vertex.y;
}

function setVertexAxisCoord(
  vertex: MeshVertex,
  line: ReturnType<typeof getProductBendLines>[number],
  value: number,
) {
  return line.orientation === 'vertical' ? { ...vertex, x: value } : { ...vertex, y: value };
}

function interpolateVertex(a: MeshVertex, b: MeshVertex, t: number): MeshVertex {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

function snapVertexToBendLine(
  vertex: MeshVertex,
  line: ReturnType<typeof getProductBendLines>[number],
  lineCoord: number,
) {
  const coord = getVertexAxisCoord(vertex, line);
  if (Math.abs(coord - lineCoord) > 0.000001) return vertex;

  return setVertexAxisCoord(vertex, line, lineCoord);
}

function clipPolygonByBendSide(
  polygon: MeshVertex[],
  line: ReturnType<typeof getProductBendLines>[number],
  lineCoord: number,
  keepPositive: boolean,
) {
  const epsilon = 0.000001;
  const output: MeshVertex[] = [];
  const isInside = (vertex: MeshVertex) => {
    const delta = getVertexAxisCoord(vertex, line) - lineCoord;
    return keepPositive ? delta >= -epsilon : delta <= epsilon;
  };

  for (let index = 0; index < polygon.length; index += 1) {
    const current = polygon[index];
    const next = polygon[(index + 1) % polygon.length];
    const currentInside = isInside(current);
    const nextInside = isInside(next);

    if (currentInside) {
      output.push(snapVertexToBendLine(current, line, lineCoord));
    }

    if (currentInside !== nextInside) {
      const currentCoord = getVertexAxisCoord(current, line);
      const nextCoord = getVertexAxisCoord(next, line);
      const span = nextCoord - currentCoord;
      if (Math.abs(span) > epsilon) {
        const intersection = interpolateVertex(current, next, (lineCoord - currentCoord) / span);
        output.push(snapVertexToBendLine(intersection, line, lineCoord));
      }
    }
  }

  return output;
}

function splitPolygonByBendLine(
  polygon: MeshPolygon,
  line: ReturnType<typeof getProductBendLines>[number],
  config: ConfiguratorState,
) {
  const lineCoord = getBendLineCoord(line, config);
  const negativeSide = clipPolygonByBendSide(polygon.points, line, lineCoord, false);
  const positiveSide = clipPolygonByBendSide(polygon.points, line, lineCoord, true);
  const next: MeshPolygon[] = [];

  if (negativeSide.length >= 3) {
    next.push({ points: negativeSide, materialIndex: polygon.materialIndex });
  }
  if (positiveSide.length >= 3) {
    next.push({ points: positiveSide, materialIndex: polygon.materialIndex });
  }

  return next.length ? next : [polygon];
}

function splitGeometryOnBendLines(
  THREE: ThreeModule,
  geometry: import('three').BufferGeometry,
  config: ConfiguratorState,
) {
  const bendLines = getProductBendLines(config).filter(line => line.angleDeg > 0);
  if (!bendLines.length) return geometry;

  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const position = source.getAttribute('position') as import('three').BufferAttribute | undefined;
  if (!position) return geometry;

  const getMaterialIndex = (vertexStart: number) =>
    source.groups.find(
      group => vertexStart >= group.start && vertexStart < group.start + group.count,
    )?.materialIndex ?? 0;

  let polygons: MeshPolygon[] = [];
  for (let index = 0; index < position.count; index += 3) {
    polygons.push({
      materialIndex: getMaterialIndex(index),
      points: [0, 1, 2].map(offset => ({
        x: position.getX(index + offset),
        y: position.getY(index + offset),
        z: position.getZ(index + offset),
      })),
    });
  }

  bendLines.forEach(line => {
    polygons = polygons.flatMap(polygon => splitPolygonByBendLine(polygon, line, config));
  });

  const byMaterial = new Map<number, MeshVertex[]>();
  polygons.forEach(polygon => {
    const triangles: MeshVertex[] = [];
    for (let index = 1; index < polygon.points.length - 1; index += 1) {
      triangles.push(polygon.points[0], polygon.points[index], polygon.points[index + 1]);
    }

    byMaterial.set(polygon.materialIndex, [
      ...(byMaterial.get(polygon.materialIndex) ?? []),
      ...triangles,
    ]);
  });

  const split = new THREE.BufferGeometry();
  const positions: number[] = [];
  Array.from(byMaterial.entries())
    .sort(([a], [b]) => a - b)
    .forEach(([materialIndex, vertices]) => {
      const start = positions.length / 3;
      vertices.forEach(vertex => positions.push(vertex.x, vertex.y, vertex.z));
      split.addGroup(start, vertices.length, materialIndex);
    });

  split.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  split.computeVertexNormals();
  split.computeBoundingBox();
  split.computeBoundingSphere();
  source.dispose();

  return split;
}

function roundDebugNumber(value: number, precision = 3) {
  if (!Number.isFinite(value)) return null;
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

function getDebugBendLines(config: ConfiguratorState): BendMeshDebugLine[] {
  return getProductBendLines(config)
    .filter(line => line.angleDeg > 0)
    .map(line => {
      const axisSizeMm = line.orientation === 'vertical' ? config.widthMm : config.lengthMm;

      return {
        ...line,
        axisSizeMm,
        centeredCoordMm: line.positionMm - axisSizeMm / 2,
      };
    });
}

function createBendPointTransformer(THREE: ThreeModule, config: ConfiguratorState) {
  const bendLines = getProductBendLines(config).filter(line => line.angleDeg > 0);
  const getFoldSide = (positionMm: number, axisSizeMm: number, direction: string) => {
    const center = axisSizeMm / 2;
    if (Math.abs(positionMm - center) < 0.001) return direction === 'down' ? 1 : -1;

    return positionMm < center ? -1 : 1;
  };
  const getLineCoord = (line: ProductBendLine) => {
    const axisSize = line.orientation === 'vertical' ? config.widthMm : config.lengthMm;
    return line.positionMm - axisSize / 2;
  };
  const getAxisSize = (line: ProductBendLine) =>
    line.orientation === 'vertical' ? config.widthMm : config.lengthMm;
  const getOriginalAxisCoord = (original: import('three').Vector3, line: ProductBendLine) =>
    line.orientation === 'vertical' ? original.x : original.y;
  const shouldFoldOriginal = (original: import('three').Vector3, line: ProductBendLine) => {
    const foldSide = getFoldSide(line.positionMm, getAxisSize(line), line.direction);
    const axisCoord = getOriginalAxisCoord(original, line);
    const lineCoord = getLineCoord(line);

    if (Math.abs(axisCoord - lineCoord) <= 0.0005) return false;

    return foldSide > 0 ? axisCoord > lineCoord : axisCoord < lineCoord;
  };
  const getNearestPreviousLineStep = (
    originalCoord: number,
    orientation: 'vertical' | 'horizontal',
    untilIndex: number,
  ) => {
    let closestDistance = Number.POSITIVE_INFINITY;
    let step = 1;

    for (let index = 0; index < untilIndex; index += 1) {
      const previous = bendLines[index];
      if (previous.orientation !== orientation) continue;

      const distance = Math.abs(originalCoord - getLineCoord(previous));
      if (distance < closestDistance) {
        closestDistance = distance;
        step = originalCoord < getLineCoord(previous) ? -1 : 1;
      }
    }

    return step;
  };

  const transformOriginalPoint = (
    original: import('three').Vector3,
    untilIndex = bendLines.length,
    decisionReference = original,
  ): import('three').Vector3 => {
    const current = original.clone();

    for (let bendIndex = 0; bendIndex < untilIndex; bendIndex += 1) {
      const line = bendLines[bendIndex];
      if (!shouldFoldOriginal(decisionReference, line)) continue;

      const foldSide = getFoldSide(line.positionMm, getAxisSize(line), line.direction);
      const directionSign = line.direction === 'down' ? -1 : 1;
      const orientationSign = line.orientation === 'vertical' ? -foldSide : foldSide;
      const lineCoord = getLineCoord(line);
      const axisStep =
        line.orientation === 'vertical'
          ? getNearestPreviousLineStep(decisionReference.y, 'horizontal', bendIndex)
          : getNearestPreviousLineStep(decisionReference.x, 'vertical', bendIndex);

      const axisStartOriginal =
        line.orientation === 'vertical'
          ? new THREE.Vector3(lineCoord, original.y, 0)
          : new THREE.Vector3(original.x, lineCoord, 0);
      const axisEndOriginal =
        line.orientation === 'vertical'
          ? new THREE.Vector3(lineCoord, original.y + axisStep, 0)
          : new THREE.Vector3(original.x + axisStep, lineCoord, 0);
      const axisStart = transformOriginalPoint(axisStartOriginal, bendIndex, decisionReference);
      const axisEnd = transformOriginalPoint(axisEndOriginal, bendIndex, decisionReference);
      const axis = axisEnd.sub(axisStart);

      if (axis.lengthSq() < 0.000001) continue;

      axis.normalize();
      current
        .sub(axisStart)
        .applyAxisAngle(
          axis,
          ((line.angleDeg * directionSign * orientationSign * axisStep) / 180) * Math.PI,
        )
        .add(axisStart);
    }

    return current;
  };

  return { bendLines, transformOriginalPoint };
}

function isPointInsidePolygon(
  points: Array<{ xMm: number; yMm: number }>,
  xMm: number,
  yMm: number,
) {
  let inside = false;

  for (
    let index = 0, previousIndex = points.length - 1;
    index < points.length;
    previousIndex = index, index += 1
  ) {
    const current = points[index];
    const previous = points[previousIndex];
    const crossesY = current.yMm > yMm !== previous.yMm > yMm;
    const crossingX =
      ((previous.xMm - current.xMm) * (yMm - current.yMm)) / (previous.yMm - current.yMm || 1) +
      current.xMm;

    if (crossesY && xMm < crossingX) inside = !inside;
  }

  return inside;
}

function isFlatPointOnMaterial(
  resolvedGeometry: ReturnType<typeof resolvePartGeometry>,
  config: ConfiguratorState,
  x: number,
  y: number,
) {
  const xMm = x + config.widthMm / 2;
  const yMm = y + config.lengthMm / 2;

  if (!isPointInsidePolygon(resolvedGeometry.finalOutlinePath.points, xMm, yMm)) return false;

  return ![...resolvedGeometry.openingPaths, ...resolvedGeometry.cutPaths]
    .filter(path => path.role !== 'edgeCut')
    .some(path => isPointInsidePolygon(path.points, xMm, yMm));
}

function getBendLinePoint(config: ConfiguratorState, line: ProductBendLine, axisMm: number) {
  return line.orientation === 'vertical'
    ? { xMm: line.positionMm, yMm: axisMm }
    : { xMm: axisMm, yMm: line.positionMm };
}

function getBendFoldSide(config: ConfiguratorState, line: ProductBendLine) {
  const axisSize = line.orientation === 'vertical' ? config.widthMm : config.lengthMm;
  const center = axisSize / 2;
  if (Math.abs(line.positionMm - center) < 0.001) return line.direction === 'down' ? 1 : -1;

  return line.positionMm < center ? -1 : 1;
}

function getBendSignedAngleAroundAxis(
  THREE: ThreeModule,
  from: import('three').Vector3,
  to: import('three').Vector3,
  axis: import('three').Vector3,
) {
  const fromPerpendicular = from.clone().sub(axis.clone().multiplyScalar(from.dot(axis)));
  const toPerpendicular = to.clone().sub(axis.clone().multiplyScalar(to.dot(axis)));

  if (fromPerpendicular.lengthSq() < 0.000001 || toPerpendicular.lengthSq() < 0.000001) {
    return 0;
  }

  fromPerpendicular.normalize();
  toPerpendicular.normalize();

  return Math.atan2(
    axis.dot(new THREE.Vector3().crossVectors(fromPerpendicular, toPerpendicular)),
    THREE.MathUtils.clamp(fromPerpendicular.dot(toPerpendicular), -1, 1),
  );
}

function getBendArcPoint(
  THREE: ThreeModule,
  center: import('three').Vector3,
  fixed: import('three').Vector3,
  folded: import('three').Vector3,
  axis: import('three').Vector3,
  progress: number,
) {
  const from = fixed.clone().sub(center);
  const to = folded.clone().sub(center);
  const angle = getBendSignedAngleAroundAxis(THREE, from, to, axis);

  if (Math.abs(angle) < 0.0001) {
    return fixed.clone().lerp(folded, progress);
  }

  return center.clone().add(from.applyAxisAngle(axis, angle * progress));
}

function isBendLineMaterialAt(
  resolvedGeometry: ReturnType<typeof resolvePartGeometry>,
  config: ConfiguratorState,
  line: ProductBendLine,
  axisMm: number,
) {
  const point = getBendLinePoint(config, line, axisMm);
  const centeredX = point.xMm - config.widthMm / 2;
  const centeredY = point.yMm - config.lengthMm / 2;

  if (isFlatPointOnMaterial(resolvedGeometry, config, centeredX, centeredY)) return true;

  const probe = Math.min(Math.max(config.thicknessMm * 0.18, 0.2), 0.8);
  const before =
    line.orientation === 'vertical'
      ? { x: centeredX - probe, y: centeredY }
      : { x: centeredX, y: centeredY - probe };
  const after =
    line.orientation === 'vertical'
      ? { x: centeredX + probe, y: centeredY }
      : { x: centeredX, y: centeredY + probe };

  return (
    isFlatPointOnMaterial(resolvedGeometry, config, before.x, before.y) &&
    isFlatPointOnMaterial(resolvedGeometry, config, after.x, after.y)
  );
}

function getBendMaterialIntervals(
  resolvedGeometry: ReturnType<typeof resolvePartGeometry>,
  config: ConfiguratorState,
  line: ProductBendLine,
) {
  const axisLength = line.orientation === 'vertical' ? config.lengthMm : config.widthMm;
  const sampleCount = Math.max(260, Math.ceil(axisLength / 1.2));
  const intervals: Array<{ startMm: number; endMm: number }> = [];
  const isInside = (axisMm: number) =>
    isBendLineMaterialAt(resolvedGeometry, config, line, Math.min(Math.max(axisMm, 0), axisLength));
  const findTransition = (fromMm: number, toMm: number, fromInside: boolean) => {
    let low = fromMm;
    let high = toMm;

    for (let index = 0; index < 18; index += 1) {
      const mid = (low + high) / 2;
      if (isInside(mid) === fromInside) low = mid;
      else high = mid;
    }

    return (low + high) / 2;
  };

  let previousMm = 0;
  let previousInside = isInside(previousMm);
  let intervalStart = previousInside ? 0 : null;

  for (let sampleIndex = 1; sampleIndex <= sampleCount; sampleIndex += 1) {
    const currentMm = (axisLength * sampleIndex) / sampleCount;
    const currentInside = isInside(currentMm);

    if (currentInside !== previousInside) {
      const transition = findTransition(previousMm, currentMm, previousInside);

      if (currentInside) intervalStart = transition;
      else if (intervalStart !== null) {
        intervals.push({ startMm: intervalStart, endMm: transition });
        intervalStart = null;
      }
    }

    previousMm = currentMm;
    previousInside = currentInside;
  }

  if (intervalStart !== null) intervals.push({ startMm: intervalStart, endMm: axisLength });

  return intervals.filter(interval => interval.endMm - interval.startMm > 0.08);
}

function createBendGuideGeometry(
  THREE: ThreeModule,
  config: ConfiguratorState,
  line: ProductBendLine,
  z: number,
  applyBends: boolean,
  transformPoint: (
    point: import('three').Vector3,
    untilIndex?: number,
    decisionReference?: import('three').Vector3,
  ) => import('three').Vector3,
) {
  const resolvedGeometry = resolvePartGeometry(config);
  const sampleCount = 180;
  const points: import('three').Vector3[] = [];

  for (let index = 0; index < sampleCount; index += 1) {
    const t0 = index / sampleCount;
    const t1 = (index + 1) / sampleCount;
    const x0 =
      line.orientation === 'horizontal'
        ? -config.widthMm / 2 + config.widthMm * t0
        : line.positionMm - config.widthMm / 2;
    const x1 =
      line.orientation === 'horizontal'
        ? -config.widthMm / 2 + config.widthMm * t1
        : line.positionMm - config.widthMm / 2;
    const y0 =
      line.orientation === 'horizontal'
        ? line.positionMm - config.lengthMm / 2
        : -config.lengthMm / 2 + config.lengthMm * t0;
    const y1 =
      line.orientation === 'horizontal'
        ? line.positionMm - config.lengthMm / 2
        : -config.lengthMm / 2 + config.lengthMm * t1;
    const midX = (x0 + x1) / 2;
    const midY = (y0 + y1) / 2;

    if (!isFlatPointOnMaterial(resolvedGeometry, config, midX, midY)) continue;

    const start = new THREE.Vector3(x0, y0, z);
    const end = new THREE.Vector3(x1, y1, z);
    const reference = new THREE.Vector3(midX, midY, z);
    points.push(
      applyBends ? transformPoint(start, undefined, reference) : start,
      applyBends ? transformPoint(end, undefined, reference) : end,
    );
  }

  return new THREE.BufferGeometry().setFromPoints(points);
}

function splitSegmentAtBendLines(
  start: { xMm: number; yMm: number },
  end: { xMm: number; yMm: number },
  bendLines: ProductBendLine[],
) {
  const stops = [0, 1];
  const epsilon = 0.000001;

  bendLines.forEach(line => {
    const startCoord = line.orientation === 'vertical' ? start.xMm : start.yMm;
    const endCoord = line.orientation === 'vertical' ? end.xMm : end.yMm;
    const delta = endCoord - startCoord;
    if (Math.abs(delta) <= epsilon) return;

    const t = (line.positionMm - startCoord) / delta;
    if (t > epsilon && t < 1 - epsilon) stops.push(t);
  });

  return Array.from(new Set(stops.map(stop => Math.round(stop * 1_000_000) / 1_000_000)))
    .sort((a, b) => a - b)
    .map(t => ({
      t,
      point: {
        xMm: start.xMm + (end.xMm - start.xMm) * t,
        yMm: start.yMm + (end.yMm - start.yMm) * t,
      },
    }));
}

function getRenderableBoundaryPaths(config: ConfiguratorState) {
  const resolvedGeometry = resolvePartGeometry(config);

  return [
    resolvedGeometry.finalOutlinePath,
    ...resolvedGeometry.openingPaths,
    ...resolvedGeometry.cutPaths.filter(path => path.role !== 'edgeCut'),
  ];
}

function createCleanPathEdgeGeometry(
  THREE: ThreeModule,
  config: ConfiguratorState,
  z: number,
  transformPoint?: (
    point: import('three').Vector3,
    untilIndex?: number,
    decisionReference?: import('three').Vector3,
  ) => import('three').Vector3,
) {
  const bendLines = transformPoint
    ? getProductBendLines(config).filter(line => line.angleDeg > 0)
    : [];
  const paths = getRenderableBoundaryPaths(config);
  const points: import('three').Vector3[] = [];
  const toVector = (point: { xMm: number; yMm: number }) =>
    new THREE.Vector3(point.xMm - config.widthMm / 2, point.yMm - config.lengthMm / 2, z);
  const addSegment = (start: { xMm: number; yMm: number }, end: { xMm: number; yMm: number }) => {
    const stops = splitSegmentAtBendLines(start, end, bendLines);

    for (let index = 0; index < stops.length - 1; index += 1) {
      const segmentStart = stops[index].point;
      const segmentEnd = stops[index + 1].point;
      const startVector = toVector(segmentStart);
      const endVector = toVector(segmentEnd);
      const referenceVector = toVector({
        xMm: (segmentStart.xMm + segmentEnd.xMm) / 2,
        yMm: (segmentStart.yMm + segmentEnd.yMm) / 2,
      });
      points.push(
        transformPoint ? transformPoint(startVector, undefined, referenceVector) : startVector,
        transformPoint ? transformPoint(endVector, undefined, referenceVector) : endVector,
      );
    }
  };

  paths.forEach(path => {
    if (path.points.length < 2) return;

    path.points.forEach((point, index) => {
      const next = path.points[index + 1];
      if (next) addSegment(point, next);
    });

    if (path.closed) {
      addSegment(path.points[path.points.length - 1], path.points[0]);
    }
  });

  return new THREE.BufferGeometry().setFromPoints(points);
}

function createBendBridgeEdgeGeometry(
  THREE: ThreeModule,
  config: ConfiguratorState,
  thickness: number,
  transformPoint: (
    point: import('three').Vector3,
    untilIndex?: number,
    decisionReference?: import('three').Vector3,
  ) => import('three').Vector3,
) {
  const resolvedGeometry = resolvePartGeometry(config);
  const bendLines = getProductBendLines(config).filter(line => line.angleDeg > 0);
  const points: import('three').Vector3[] = [];
  const topZ = thickness / 2 + 0.12;
  const referenceOffsetMm = 0.25;
  const toVector = (point: { xMm: number; yMm: number }, z: number) =>
    new THREE.Vector3(point.xMm - config.widthMm / 2, point.yMm - config.lengthMm / 2, z);
  const appendArc = (line: ProductBendLine, axisMm: number, axisLength: number) => {
    const foldSide = getBendFoldSide(config, line);
    const point = getBendLinePoint(config, line, axisMm);
    const adjacentAxisMm =
      axisMm < axisLength - 1 ? Math.min(axisMm + 1, axisLength) : Math.max(axisMm - 1, 0);
    const adjacentPoint = getBendLinePoint(config, line, adjacentAxisMm);
    const centeredX = point.xMm - config.widthMm / 2;
    const centeredY = point.yMm - config.lengthMm / 2;

    const fixedReference =
      line.orientation === 'vertical'
        ? new THREE.Vector3(centeredX - foldSide * referenceOffsetMm, centeredY, 0)
        : new THREE.Vector3(centeredX, centeredY - foldSide * referenceOffsetMm, 0);
    const foldedReference =
      line.orientation === 'vertical'
        ? new THREE.Vector3(centeredX + foldSide * referenceOffsetMm, centeredY, 0)
        : new THREE.Vector3(centeredX, centeredY + foldSide * referenceOffsetMm, 0);
    const fixedTop = transformPoint(toVector(point, topZ), undefined, fixedReference);
    const foldedTop = transformPoint(toVector(point, topZ), undefined, foldedReference);
    const fixedCenter = transformPoint(toVector(point, 0), undefined, fixedReference);
    const foldedCenter = transformPoint(toVector(point, 0), undefined, foldedReference);
    const center = fixedCenter.clone().lerp(foldedCenter, 0.5);

    const adjacentCenteredX = adjacentPoint.xMm - config.widthMm / 2;
    const adjacentCenteredY = adjacentPoint.yMm - config.lengthMm / 2;
    const adjacentFixedReference =
      line.orientation === 'vertical'
        ? new THREE.Vector3(adjacentCenteredX - foldSide * referenceOffsetMm, adjacentCenteredY, 0)
        : new THREE.Vector3(adjacentCenteredX, adjacentCenteredY - foldSide * referenceOffsetMm, 0);
    const adjacentFoldedReference =
      line.orientation === 'vertical'
        ? new THREE.Vector3(adjacentCenteredX + foldSide * referenceOffsetMm, adjacentCenteredY, 0)
        : new THREE.Vector3(adjacentCenteredX, adjacentCenteredY + foldSide * referenceOffsetMm, 0);
    const adjacentFixedCenter = transformPoint(
      toVector(adjacentPoint, 0),
      undefined,
      adjacentFixedReference,
    );
    const adjacentFoldedCenter = transformPoint(
      toVector(adjacentPoint, 0),
      undefined,
      adjacentFoldedReference,
    );
    const adjacentCenter = adjacentFixedCenter.clone().lerp(adjacentFoldedCenter, 0.5);
    const axis = adjacentCenter.sub(center);

    if (axis.lengthSq() < 0.000001) return;

    axis.normalize();

    const arcSegments = Math.max(5, Math.ceil(Math.abs(line.angleDeg) / 12));
    let previous = getBendArcPoint(THREE, center, fixedTop, foldedTop, axis, 0);

    for (let index = 1; index <= arcSegments; index += 1) {
      const next = getBendArcPoint(THREE, center, fixedTop, foldedTop, axis, index / arcSegments);
      points.push(previous, next);
      previous = next;
    }
  };

  bendLines.forEach(line => {
    const axisLength = line.orientation === 'vertical' ? config.lengthMm : config.widthMm;
    const intervalKeys = new Set<string>();

    getBendMaterialIntervals(resolvedGeometry, config, line).forEach(interval => {
      [interval.startMm, interval.endMm].forEach(axisMm => {
        const clampedAxisMm = clamp(axisMm, 0, axisLength);
        const key = `${line.id}:${Math.round(clampedAxisMm * 1000)}`;
        if (intervalKeys.has(key)) return;

        intervalKeys.add(key);
        appendArc(line, clampedAxisMm, axisLength);
      });
    });
  });

  return new THREE.BufferGeometry().setFromPoints(points);
}

function createFoldedSheetGeometry(
  THREE: ThreeModule,
  config: ConfiguratorState,
  thickness: number,
  transformPoint: (
    point: import('three').Vector3,
    untilIndex?: number,
    decisionReference?: import('three').Vector3,
  ) => import('three').Vector3,
) {
  const resolvedGeometry = resolvePartGeometry(config);
  const faceGeometry = new THREE.ShapeGeometry(buildPlateShape(THREE, config), 48);
  const splitFaceGeometry = splitGeometryOnBendLines(THREE, faceGeometry, config);
  const source = splitFaceGeometry.index
    ? splitFaceGeometry.toNonIndexed()
    : splitFaceGeometry.clone();
  const position = source.getAttribute('position') as import('three').BufferAttribute | undefined;
  const positions: number[] = [];
  const pushVector = (vector: import('three').Vector3) => {
    positions.push(vector.x, vector.y, vector.z);
  };
  const pushTriangle = (
    a: import('three').Vector3,
    b: import('three').Vector3,
    c: import('three').Vector3,
  ) => {
    pushVector(a);
    pushVector(b);
    pushVector(c);
  };
  const pushTriangleIfValid = (
    a: import('three').Vector3,
    b: import('three').Vector3,
    c: import('three').Vector3,
  ) => {
    const area =
      new THREE.Vector3().subVectors(b, a).cross(new THREE.Vector3().subVectors(c, a)).length() / 2;
    if (area < 0.0001) return;

    pushTriangle(a, b, c);
  };

  if (position) {
    for (let index = 0; index < position.count; index += 3) {
      const originals = [0, 1, 2].map(
        offset =>
          new THREE.Vector3(position.getX(index + offset), position.getY(index + offset), 0),
      );
      const reference = originals
        .reduce((total, point) => total.add(point), new THREE.Vector3())
        .multiplyScalar(1 / originals.length);
      const top = originals.map(point =>
        transformPoint(new THREE.Vector3(point.x, point.y, thickness / 2), undefined, reference),
      );
      const bottom = originals.map(point =>
        transformPoint(new THREE.Vector3(point.x, point.y, -thickness / 2), undefined, reference),
      );

      pushTriangleIfValid(top[0], top[1], top[2]);
      pushTriangleIfValid(bottom[2], bottom[1], bottom[0]);
    }
  }

  const surfaceVertexCount = positions.length / 3;
  const bendLines = getProductBendLines(config).filter(line => line.angleDeg > 0);
  const toVector = (point: { xMm: number; yMm: number }, z: number) =>
    new THREE.Vector3(point.xMm - config.widthMm / 2, point.yMm - config.lengthMm / 2, z);
  const pushSideWall = (
    segmentStart: { xMm: number; yMm: number },
    segmentEnd: { xMm: number; yMm: number },
  ) => {
    const reference = toVector(
      {
        xMm: (segmentStart.xMm + segmentEnd.xMm) / 2,
        yMm: (segmentStart.yMm + segmentEnd.yMm) / 2,
      },
      0,
    );
    const topStart = transformPoint(toVector(segmentStart, thickness / 2), undefined, reference);
    const topEnd = transformPoint(toVector(segmentEnd, thickness / 2), undefined, reference);
    const bottomStart = transformPoint(
      toVector(segmentStart, -thickness / 2),
      undefined,
      reference,
    );
    const bottomEnd = transformPoint(toVector(segmentEnd, -thickness / 2), undefined, reference);

    pushTriangleIfValid(topStart, bottomStart, bottomEnd);
    pushTriangleIfValid(topStart, bottomEnd, topEnd);
  };
  const getFoldSide = (line: ProductBendLine) => {
    const axisSize = line.orientation === 'vertical' ? config.widthMm : config.lengthMm;
    const center = axisSize / 2;
    if (Math.abs(line.positionMm - center) < 0.001) return line.direction === 'down' ? 1 : -1;

    return line.positionMm < center ? -1 : 1;
  };
  const pushBridgeQuad = (
    fixedStart: import('three').Vector3,
    fixedEnd: import('three').Vector3,
    foldedStart: import('three').Vector3,
    foldedEnd: import('three').Vector3,
  ) => {
    pushTriangleIfValid(fixedStart, foldedStart, foldedEnd);
    pushTriangleIfValid(fixedStart, foldedEnd, fixedEnd);
  };
  const getSignedAngleAroundAxis = (
    from: import('three').Vector3,
    to: import('three').Vector3,
    axis: import('three').Vector3,
  ) => {
    const fromPerpendicular = from.clone().sub(axis.clone().multiplyScalar(from.dot(axis)));
    const toPerpendicular = to.clone().sub(axis.clone().multiplyScalar(to.dot(axis)));

    if (fromPerpendicular.lengthSq() < 0.000001 || toPerpendicular.lengthSq() < 0.000001) {
      return 0;
    }

    fromPerpendicular.normalize();
    toPerpendicular.normalize();

    return Math.atan2(
      axis.dot(new THREE.Vector3().crossVectors(fromPerpendicular, toPerpendicular)),
      THREE.MathUtils.clamp(fromPerpendicular.dot(toPerpendicular), -1, 1),
    );
  };
  const getArcPoint = (
    center: import('three').Vector3,
    fixed: import('three').Vector3,
    folded: import('three').Vector3,
    axis: import('three').Vector3,
    progress: number,
  ) => {
    const from = fixed.clone().sub(center);
    const to = folded.clone().sub(center);
    const angle = getSignedAngleAroundAxis(from, to, axis);

    if (Math.abs(angle) < 0.0001) {
      return fixed.clone().lerp(folded, progress);
    }

    return center.clone().add(from.applyAxisAngle(axis, angle * progress));
  };
  const pushCurvedBendBridge = (
    centerStart: import('three').Vector3,
    centerEnd: import('three').Vector3,
    fixedTopStart: import('three').Vector3,
    fixedTopEnd: import('three').Vector3,
    foldedTopStart: import('three').Vector3,
    foldedTopEnd: import('three').Vector3,
    fixedBottomStart: import('three').Vector3,
    fixedBottomEnd: import('three').Vector3,
    foldedBottomStart: import('three').Vector3,
    foldedBottomEnd: import('three').Vector3,
    angleDeg: number,
    capStart: boolean,
    capEnd: boolean,
  ) => {
    const axis = new THREE.Vector3().subVectors(centerEnd, centerStart);
    if (axis.lengthSq() < 0.000001) return;
    axis.normalize();

    const arcSegments = Math.max(5, Math.ceil(Math.abs(angleDeg) / 12));

    for (let segmentIndex = 0; segmentIndex < arcSegments; segmentIndex += 1) {
      const previousProgress = segmentIndex / arcSegments;
      const nextProgress = (segmentIndex + 1) / arcSegments;

      const previousTopStart = getArcPoint(
        centerStart,
        fixedTopStart,
        foldedTopStart,
        axis,
        previousProgress,
      );
      const previousTopEnd = getArcPoint(
        centerEnd,
        fixedTopEnd,
        foldedTopEnd,
        axis,
        previousProgress,
      );
      const nextTopStart = getArcPoint(
        centerStart,
        fixedTopStart,
        foldedTopStart,
        axis,
        nextProgress,
      );
      const nextTopEnd = getArcPoint(centerEnd, fixedTopEnd, foldedTopEnd, axis, nextProgress);

      const previousBottomStart = getArcPoint(
        centerStart,
        fixedBottomStart,
        foldedBottomStart,
        axis,
        previousProgress,
      );
      const previousBottomEnd = getArcPoint(
        centerEnd,
        fixedBottomEnd,
        foldedBottomEnd,
        axis,
        previousProgress,
      );
      const nextBottomStart = getArcPoint(
        centerStart,
        fixedBottomStart,
        foldedBottomStart,
        axis,
        nextProgress,
      );
      const nextBottomEnd = getArcPoint(
        centerEnd,
        fixedBottomEnd,
        foldedBottomEnd,
        axis,
        nextProgress,
      );

      pushBridgeQuad(previousTopStart, previousTopEnd, nextTopStart, nextTopEnd);
      pushBridgeQuad(previousBottomEnd, previousBottomStart, nextBottomEnd, nextBottomStart);
      if (capStart) {
        pushBridgeQuad(previousTopStart, nextTopStart, previousBottomStart, nextBottomStart);
      }
      if (capEnd) {
        pushBridgeQuad(previousTopEnd, previousBottomEnd, nextTopEnd, nextBottomEnd);
      }
    }
  };
  const pushBendBridge = (line: ProductBendLine) => {
    const axisLength = line.orientation === 'vertical' ? config.lengthMm : config.widthMm;
    const capBoundaryToleranceMm = 0.6;
    const foldSide = getFoldSide(line);
    const referenceOffsetMm = 0.25;
    const intervals = getBendMaterialIntervals(resolvedGeometry, config, line);

    intervals.forEach(interval => {
      const segmentCount = Math.max(1, Math.ceil((interval.endMm - interval.startMm) / 3));

      for (let index = 0; index < segmentCount; index += 1) {
        const startAxisMm =
          interval.startMm + ((interval.endMm - interval.startMm) * index) / segmentCount;
        const endAxisMm =
          interval.startMm + ((interval.endMm - interval.startMm) * (index + 1)) / segmentCount;

        const start = getBendLinePoint(config, line, startAxisMm);
        const end = getBendLinePoint(config, line, endAxisMm);
        const mid = {
          xMm: (start.xMm + end.xMm) / 2,
          yMm: (start.yMm + end.yMm) / 2,
        };
        const centeredMidX = mid.xMm - config.widthMm / 2;
        const centeredMidY = mid.yMm - config.lengthMm / 2;

        const fixedReference =
          line.orientation === 'vertical'
            ? new THREE.Vector3(centeredMidX - foldSide * referenceOffsetMm, centeredMidY, 0)
            : new THREE.Vector3(centeredMidX, centeredMidY - foldSide * referenceOffsetMm, 0);
        const foldedReference =
          line.orientation === 'vertical'
            ? new THREE.Vector3(centeredMidX + foldSide * referenceOffsetMm, centeredMidY, 0)
            : new THREE.Vector3(centeredMidX, centeredMidY + foldSide * referenceOffsetMm, 0);
        const fixedTopStart = transformPoint(
          toVector(start, thickness / 2),
          undefined,
          fixedReference,
        );
        const fixedTopEnd = transformPoint(toVector(end, thickness / 2), undefined, fixedReference);
        const foldedTopStart = transformPoint(
          toVector(start, thickness / 2),
          undefined,
          foldedReference,
        );
        const foldedTopEnd = transformPoint(
          toVector(end, thickness / 2),
          undefined,
          foldedReference,
        );
        const fixedBottomStart = transformPoint(
          toVector(start, -thickness / 2),
          undefined,
          fixedReference,
        );
        const fixedBottomEnd = transformPoint(
          toVector(end, -thickness / 2),
          undefined,
          fixedReference,
        );
        const foldedBottomStart = transformPoint(
          toVector(start, -thickness / 2),
          undefined,
          foldedReference,
        );
        const foldedBottomEnd = transformPoint(
          toVector(end, -thickness / 2),
          undefined,
          foldedReference,
        );
        const fixedCenterStart = transformPoint(toVector(start, 0), undefined, fixedReference);
        const fixedCenterEnd = transformPoint(toVector(end, 0), undefined, fixedReference);
        const foldedCenterStart = transformPoint(toVector(start, 0), undefined, foldedReference);
        const foldedCenterEnd = transformPoint(toVector(end, 0), undefined, foldedReference);
        const centerStart = fixedCenterStart.clone().lerp(foldedCenterStart, 0.5);
        const centerEnd = fixedCenterEnd.clone().lerp(foldedCenterEnd, 0.5);

        pushCurvedBendBridge(
          centerStart,
          centerEnd,
          fixedTopStart,
          fixedTopEnd,
          foldedTopStart,
          foldedTopEnd,
          fixedBottomStart,
          fixedBottomEnd,
          foldedBottomStart,
          foldedBottomEnd,
          line.angleDeg,
          index === 0 && interval.startMm <= capBoundaryToleranceMm,
          index === segmentCount - 1 && interval.endMm >= axisLength - capBoundaryToleranceMm,
        );
      }
    });
  };

  getRenderableBoundaryPaths(config).forEach(path => {
    if (path.points.length < 2) return;

    path.points.forEach((point, index) => {
      const next = path.points[index + 1];
      if (!next) return;

      const stops = splitSegmentAtBendLines(point, next, bendLines);
      for (let stopIndex = 0; stopIndex < stops.length - 1; stopIndex += 1) {
        pushSideWall(stops[stopIndex].point, stops[stopIndex + 1].point);
      }
    });

    if (!path.closed) return;

    const stops = splitSegmentAtBendLines(
      path.points[path.points.length - 1],
      path.points[0],
      bendLines,
    );
    for (let stopIndex = 0; stopIndex < stops.length - 1; stopIndex += 1) {
      pushSideWall(stops[stopIndex].point, stops[stopIndex + 1].point);
    }
  });

  bendLines.forEach(pushBendBridge);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.addGroup(0, surfaceVertexCount, 0);
  geometry.addGroup(surfaceVertexCount, positions.length / 3 - surfaceVertexCount, 1);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  source.dispose();
  if (splitFaceGeometry !== faceGeometry) splitFaceGeometry.dispose();
  faceGeometry.dispose();

  return geometry;
}

function removeBentMeshArtifacts(THREE: ThreeModule, geometry: import('three').BufferGeometry) {
  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const position = source.getAttribute('position') as import('three').BufferAttribute | undefined;
  if (!position) {
    source.dispose();
    return geometry;
  }

  const byMaterial = new Map<number, MeshVertex[]>();

  for (let index = 0; index < position.count; index += 3) {
    const points = [0, 1, 2].map(offset => ({
      x: position.getX(index + offset),
      y: position.getY(index + offset),
      z: position.getZ(index + offset),
    }));
    const edgeLengths = [
      getVertexDistance(points[0], points[1]),
      getVertexDistance(points[1], points[2]),
      getVertexDistance(points[2], points[0]),
    ];
    const minEdge = Math.min(...edgeLengths);
    const maxEdge = Math.max(...edgeLengths);
    const area = getTriangleArea(points);
    const materialIndex = getMaterialIndexForTriangle(source, index);
    const isArtifact =
      area < 0.01 ||
      minEdge < 0.000001 ||
      (minEdge < 0.12 && maxEdge / Math.max(minEdge, 0.000001) > 80) ||
      (materialIndex === 1 && area < 2 && maxEdge > 10);

    if (isArtifact) continue;

    byMaterial.set(materialIndex, [...(byMaterial.get(materialIndex) ?? []), ...points]);
  }

  const cleaned = new THREE.BufferGeometry();
  const positions: number[] = [];
  Array.from(byMaterial.entries())
    .sort(([a], [b]) => a - b)
    .forEach(([materialIndex, vertices]) => {
      const start = positions.length / 3;
      vertices.forEach(vertex => positions.push(vertex.x, vertex.y, vertex.z));
      cleaned.addGroup(start, vertices.length, materialIndex);
    });

  cleaned.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  cleaned.computeVertexNormals();
  cleaned.computeBoundingBox();
  cleaned.computeBoundingSphere();
  source.dispose();

  return cleaned;
}

function getMaterialIndexForTriangle(
  geometry: import('three').BufferGeometry,
  vertexStart: number,
) {
  return (
    geometry.groups.find(
      group => vertexStart >= group.start && vertexStart < group.start + group.count,
    )?.materialIndex ?? 0
  );
}

function getTriangleBounds(points: MeshVertex[]) {
  return {
    minX: roundDebugNumber(Math.min(...points.map(point => point.x))) ?? 0,
    maxX: roundDebugNumber(Math.max(...points.map(point => point.x))) ?? 0,
    minY: roundDebugNumber(Math.min(...points.map(point => point.y))) ?? 0,
    maxY: roundDebugNumber(Math.max(...points.map(point => point.y))) ?? 0,
    minZ: roundDebugNumber(Math.min(...points.map(point => point.z))) ?? 0,
    maxZ: roundDebugNumber(Math.max(...points.map(point => point.z))) ?? 0,
  };
}

function getVertexDistance(a: MeshVertex, b: MeshVertex) {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function getTriangleArea(points: MeshVertex[]) {
  const [a, b, c] = points;
  const ab = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
  const ac = { x: c.x - a.x, y: c.y - a.y, z: c.z - a.z };
  const cross = {
    x: ab.y * ac.z - ab.z * ac.y,
    y: ab.z * ac.x - ab.x * ac.z,
    z: ab.x * ac.y - ab.y * ac.x,
  };

  return Math.hypot(cross.x, cross.y, cross.z) / 2;
}

function getTriangleBendCrossings(points: MeshVertex[], bendLines: BendMeshDebugLine[]) {
  const epsilon = 0.01;

  return bendLines
    .filter(line => {
      const coords = points.map(point => (line.orientation === 'vertical' ? point.x : point.y));
      return (
        Math.min(...coords) < line.centeredCoordMm - epsilon &&
        Math.max(...coords) > line.centeredCoordMm + epsilon
      );
    })
    .map(line => line.id);
}

function summarizeMeshGeometry(
  THREE: ThreeModule,
  label: string,
  geometry: import('three').BufferGeometry,
  bendLines: BendMeshDebugLine[],
  config: ConfiguratorState,
): MeshDebugSummary {
  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const position = source.getAttribute('position') as import('three').BufferAttribute | undefined;
  const triangles: MeshTriangleDebug[] = [];
  const longEdgeThreshold = Math.max(config.widthMm, config.lengthMm) * 0.42;
  let degenerateTriangleCount = 0;
  let skinnyTriangleCount = 0;

  if (position) {
    for (let index = 0; index < position.count; index += 3) {
      const points = [0, 1, 2].map(offset => ({
        x: position.getX(index + offset),
        y: position.getY(index + offset),
        z: position.getZ(index + offset),
      }));
      const edgeLengths = [
        getVertexDistance(points[0], points[1]),
        getVertexDistance(points[1], points[2]),
        getVertexDistance(points[2], points[0]),
      ];
      const minEdge = Math.min(...edgeLengths);
      const maxEdge = Math.max(...edgeLengths);
      const area = getTriangleArea(points);
      const crossedBendLines = getTriangleBendCrossings(points, bendLines);

      if (area < 0.0001) degenerateTriangleCount += 1;
      if (minEdge > 0 && maxEdge / minEdge > 90) skinnyTriangleCount += 1;

      if (maxEdge >= longEdgeThreshold || crossedBendLines.length > 0 || area < 0.0001) {
        triangles.push({
          triangleIndex: index / 3,
          materialIndex: getMaterialIndexForTriangle(source, index),
          areaMm2: roundDebugNumber(area) ?? 0,
          edgeLengthsMm: edgeLengths.map(length => roundDebugNumber(length) ?? 0),
          bounds: getTriangleBounds(points),
          crossedBendLines,
        });
      }
    }
  }

  source.computeBoundingBox();
  const bounds = source.boundingBox;
  const bendCrossingTriangles = triangles
    .filter(triangle => triangle.crossedBendLines.length > 0)
    .sort((a, b) => Math.max(...b.edgeLengthsMm) - Math.max(...a.edgeLengthsMm))
    .slice(0, 30);
  const longestEdges = triangles
    .sort((a, b) => Math.max(...b.edgeLengthsMm) - Math.max(...a.edgeLengthsMm))
    .slice(0, 30);

  const summary: MeshDebugSummary = {
    label,
    indexed: Boolean(geometry.index),
    vertexCount: position?.count ?? 0,
    triangleCount: position ? Math.floor(position.count / 3) : 0,
    groupCount: source.groups.length,
    materialGroups: source.groups.map(group => ({
      materialIndex: group.materialIndex,
      start: group.start,
      count: group.count,
    })),
    bounds: {
      minX: bounds ? roundDebugNumber(bounds.min.x) : null,
      maxX: bounds ? roundDebugNumber(bounds.max.x) : null,
      minY: bounds ? roundDebugNumber(bounds.min.y) : null,
      maxY: bounds ? roundDebugNumber(bounds.max.y) : null,
      minZ: bounds ? roundDebugNumber(bounds.min.z) : null,
      maxZ: bounds ? roundDebugNumber(bounds.max.z) : null,
    },
    degenerateTriangleCount,
    skinnyTriangleCount,
    bendCrossingTriangleCount: bendCrossingTriangles.length,
    longestEdges,
    bendCrossingTriangles,
  };

  source.dispose();

  return summary;
}

export async function createBendMeshDebugReport(config: ConfiguratorState) {
  const THREE = await import('three');
  const thickness = Math.max(config.thicknessMm, 1);
  const bendLines = getDebugBendLines(config);
  const plateShape = buildPlateShape(THREE, config);
  const flatGeometry = new THREE.ExtrudeGeometry(plateShape, {
    depth: thickness,
    bevelEnabled: false,
    curveSegments: 48,
  });
  flatGeometry.center();

  const splitGeometry = splitGeometryOnBendLines(THREE, flatGeometry, config);
  const bentGeometry = splitGeometry.clone();
  applyBendLinesToGeometry(THREE, bentGeometry, config);
  const cleanedBentGeometry = removeBentMeshArtifacts(THREE, bentGeometry);
  const { transformOriginalPoint } = createBendPointTransformer(THREE, config);
  const foldedPreviewGeometry = createFoldedSheetGeometry(
    THREE,
    config,
    Math.max(config.thicknessMm, 1),
    transformOriginalPoint,
  );

  const report = {
    note: 'Diagnostika 3D ohybov. Preview pri aplikovanych ohyboch pouziva foldedPreviewGeometry: samostatne panelove plochy a bocne steny obrysu/vyrezov.',
    bendLines,
    flatGeometry: summarizeMeshGeometry(
      THREE,
      'flat-before-split',
      flatGeometry,
      bendLines,
      config,
    ),
    splitGeometry: summarizeMeshGeometry(
      THREE,
      'flat-after-split',
      splitGeometry,
      bendLines,
      config,
    ),
    bentGeometry: summarizeMeshGeometry(THREE, 'bent-after-apply', bentGeometry, bendLines, config),
    cleanedBentGeometry: summarizeMeshGeometry(
      THREE,
      'bent-after-cleanup',
      cleanedBentGeometry,
      bendLines,
      config,
    ),
    foldedPreviewGeometry: summarizeMeshGeometry(
      THREE,
      'folded-preview-panel-sheet',
      foldedPreviewGeometry,
      bendLines,
      config,
    ),
  };

  foldedPreviewGeometry.dispose();
  if (cleanedBentGeometry !== bentGeometry) cleanedBentGeometry.dispose();
  bentGeometry.dispose();
  if (splitGeometry !== flatGeometry) splitGeometry.dispose();
  flatGeometry.dispose();

  return report;
}

function applyBendLinesToGeometry(
  THREE: ThreeModule,
  geometry: import('three').BufferGeometry,
  config: ConfiguratorState,
) {
  const { bendLines, transformOriginalPoint } = createBendPointTransformer(THREE, config);
  const positionAttribute = geometry.getAttribute('position');
  if (!bendLines.length || !positionAttribute) return;

  const position = positionAttribute as import('three').BufferAttribute;

  for (let index = 0; index < position.count; index += 3) {
    const originals = [0, 1, 2]
      .filter(offset => index + offset < position.count)
      .map(
        offset =>
          new THREE.Vector3(
            position.getX(index + offset),
            position.getY(index + offset),
            position.getZ(index + offset),
          ),
      );
    const reference = originals
      .reduce((total, point) => total.add(point), new THREE.Vector3())
      .multiplyScalar(1 / Math.max(originals.length, 1));

    originals.forEach((original, offset) => {
      const vector = transformOriginalPoint(original, bendLines.length, reference);
      position.setXYZ(index + offset, vector.x, vector.y, vector.z);
    });
  }

  position.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

function shadeHex(hex: string, amount: number) {
  const color = hexToRgb(hex);
  const mix = amount >= 0 ? 255 : 0;
  const ratio = Math.abs(amount);
  const r = Math.round(color.r + (mix - color.r) * ratio);
  const g = Math.round(color.g + (mix - color.g) * ratio);
  const b = Math.round(color.b + (mix - color.b) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

function PlatePresetThumbnail({
  config,
  preset,
}: {
  config: ConfiguratorState;
  preset: ViewPreset;
}) {
  const { finish } = getSelectedOptions(config);
  const frontColor = finish.color;
  const sideColor = shadeHex(finish.color, -0.34);
  const topColor = shadeHex(finish.color, 0.44);
  const stroke = '#171717';

  if (preset === 'front') {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true" className="h-8 w-8">
        <rect x="8" y="13" width="32" height="22" rx="2" fill={frontColor} />
        <rect
          x="8"
          y="13"
          width="32"
          height="22"
          rx="2"
          fill="url(#preset-front-sheen)"
          opacity="0.2"
        />
        <rect
          x="8"
          y="13"
          width="32"
          height="22"
          rx="2"
          fill="none"
          stroke={stroke}
          strokeWidth="2"
        />
        {[
          [16, 20],
          [32, 20],
          [16, 28],
          [32, 28],
        ].map(([cx, cy]) => (
          <circle
            key={`${cx}-${cy}`}
            cx={cx}
            cy={cy}
            r="2.4"
            fill="white"
            stroke={stroke}
            strokeWidth="1.4"
          />
        ))}
        <defs>
          <linearGradient
            id="preset-front-sheen"
            x1="8"
            x2="40"
            y1="13"
            y2="35"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    );
  }

  if (preset === 'vertical') {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true" className="h-8 w-8">
        <polygon
          points="18,8 29,13 29,40 18,35"
          fill={frontColor}
          stroke={stroke}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <polygon
          points="29,13 35,10 35,37 29,40"
          fill={sideColor}
          stroke={stroke}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <polygon
          points="18,8 24,5 35,10 29,13"
          fill={topColor}
          stroke={stroke}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <line
          x1="22"
          y1="14"
          x2="22"
          y2="31"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
          opacity="0.65"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className="h-8 w-8">
      <polygon
        points="9,19 32,19 40,25 17,25"
        fill={frontColor}
        stroke={stroke}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <polygon
        points="17,25 40,25 40,31 17,31"
        fill={sideColor}
        stroke={stroke}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <polygon
        points="9,19 17,25 17,31 9,25"
        fill={topColor}
        stroke={stroke}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="19" cy="22" r="2" fill="white" stroke={stroke} strokeWidth="1.2" />
      <circle cx="30" cy="22" r="2" fill="white" stroke={stroke} strokeWidth="1.2" />
    </svg>
  );
}
export function buildPlateShape(THREE: ThreeModule, config: ConfiguratorState) {
  const width = config.widthMm;
  const length = config.lengthMm;
  const resolvedGeometry = resolvePartGeometry(config);
  const shape = new THREE.Shape();
  const firstPoint = resolvedGeometry.finalOutlinePath.points[0];

  if (firstPoint) {
    shape.moveTo(firstPoint.xMm - width / 2, firstPoint.yMm - length / 2);
    resolvedGeometry.finalOutlinePath.points.slice(1).forEach(point => {
      shape.lineTo(point.xMm - width / 2, point.yMm - length / 2);
    });
    shape.closePath();
  }

  [...resolvedGeometry.openingPaths, ...resolvedGeometry.cutPaths]
    .filter(path => path.role !== 'edgeCut')
    .forEach(path => {
      shape.holes.push(geometryPathToThreePath(THREE, path, width, length));
    });

  return shape;
}

export function buildSceneObjects(
  THREE: ThreeModule,
  config: ConfiguratorState,
  options: {
    holeVisualScale?: number;
    showBendGuides?: boolean;
    applyBends?: boolean;
    hideBendGuidesWhenApplied?: boolean;
    documentRender?: boolean;
  } = {},
) {
  const { finish, product } = getSelectedOptions(config);
  const group = new THREE.Group();
  const thickness = Math.max(config.thicknessMm, 1);
  const edgeRadius = 0;
  const bevelSize = 0;
  const plateShape = buildPlateShape(THREE, config);
  const metalColor = product.features.includes('bend') ? '#8c3333' : shadeHex(finish.color, -0.22);
  const sideMaterial = new THREE.MeshStandardMaterial({
    color: metalColor,
    roughness: 0.7,
    metalness: 0.22,
    side: THREE.DoubleSide,
  });
  const frontMaterial = new THREE.MeshStandardMaterial({
    color: finish.color,
    roughness: finish.id === 'zinc' || finish.id === 'raw' ? 0.5 : 0.62,
    metalness: finish.id === 'zinc' || finish.id === 'raw' ? 0.42 : 0.16,
    side: THREE.DoubleSide,
  });
  const { transformOriginalPoint } = createBendPointTransformer(THREE, config);
  const geometry: import('three').BufferGeometry = options.applyBends
    ? createFoldedSheetGeometry(THREE, config, thickness, transformOriginalPoint)
    : new THREE.ExtrudeGeometry(plateShape, {
        depth: thickness,
        bevelEnabled: edgeRadius > 0,
        bevelSize,
        bevelThickness: Math.min(bevelSize * 0.75, thickness * 0.25),
        bevelSegments: edgeRadius > 0 ? 4 : 1,
        curveSegments: 48,
      });
  if (!options.applyBends) geometry.center();
  geometry.computeVertexNormals();

  const body = new THREE.Mesh(geometry, [frontMaterial, sideMaterial]);
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  if (options.documentRender) {
    const edgeGeometry = new THREE.EdgesGeometry(geometry, 32);
    const edges = new THREE.LineSegments(
      edgeGeometry,
      new THREE.LineBasicMaterial({
        color: '#181818',
        transparent: false,
        depthTest: true,
        depthWrite: false,
      }),
    );
    edges.renderOrder = 2;
    group.add(edges);
  } else {
    const edgeGeometry = createCleanPathEdgeGeometry(
      THREE,
      config,
      thickness / 2 + 0.08,
      options.applyBends ? transformOriginalPoint : undefined,
    );
    const edges = new THREE.LineSegments(
      edgeGeometry,
      new THREE.LineBasicMaterial({
        color: '#171717',
        transparent: true,
        opacity: options.applyBends ? 0.58 : 0.48,
      }),
    );
    edges.renderOrder = options.applyBends ? 8 : 1;
    group.add(edges);
  }

  if (options.applyBends) {
    const bendEdgeGeometry = createBendBridgeEdgeGeometry(
      THREE,
      config,
      thickness,
      transformOriginalPoint,
    );
    const bendEdges = new THREE.LineSegments(
      bendEdgeGeometry,
      new THREE.LineBasicMaterial({
        color: '#111111',
        transparent: !options.documentRender,
        opacity: options.documentRender ? 1 : 0.68,
        depthTest: true,
        depthWrite: false,
      }),
    );
    bendEdges.renderOrder = 9;
    group.add(bendEdges);
  }

  if (options.showBendGuides && (!options.applyBends || !options.hideBendGuidesWhenApplied)) {
    const bendMaterial = new THREE.LineDashedMaterial({
      color: '#2563eb',
      dashSize: options.applyBends ? 5 : 14,
      gapSize: options.applyBends ? 4 : 8,
      linewidth: 1,
      transparent: true,
      opacity: options.applyBends ? 0.72 : 0.92,
      depthTest: false,
    });
    const labelMaterial = new THREE.MeshBasicMaterial({
      color: '#2563eb',
      transparent: true,
      opacity: 0.22,
      depthTest: false,
    });
    const z = thickness / 2 + 1.4;

    getProductBendLines(config).forEach(line => {
      const guideGeometry = createBendGuideGeometry(
        THREE,
        config,
        line,
        z,
        Boolean(options.applyBends),
        transformOriginalPoint,
      );

      const guide = new THREE.LineSegments(guideGeometry, bendMaterial);
      guide.computeLineDistances();
      guide.renderOrder = 10;
      group.add(guide);

      if (options.applyBends) return;

      const markerSize = Math.max(Math.min(config.widthMm, config.lengthMm) * 0.018, 3);
      const markerGeometry = new THREE.PlaneGeometry(
        line.orientation === 'horizontal' ? markerSize * 7 : markerSize * 1.2,
        line.orientation === 'horizontal' ? markerSize * 1.2 : markerSize * 7,
      );
      markerGeometry.translate(
        line.orientation === 'horizontal'
          ? -config.widthMm * 0.38
          : line.positionMm - config.widthMm / 2,
        line.orientation === 'horizontal'
          ? line.positionMm - config.lengthMm / 2
          : -config.lengthMm * 0.38,
        z + 0.2,
      );
      const marker = new THREE.Mesh(markerGeometry, labelMaterial);
      marker.renderOrder = 9;
      group.add(marker);
    });
  }

  if (product.visual.kind === 'bracket') {
    const ribShape = new THREE.Shape();
    const ribSize = Math.min(config.widthMm, config.lengthMm) * 0.46;
    ribShape.moveTo(0, 0);
    ribShape.lineTo(ribSize, 0);
    ribShape.lineTo(ribSize, ribSize * 0.82);
    ribShape.closePath();
    const ribGeometry = new THREE.ExtrudeGeometry(ribShape, {
      depth: thickness,
      bevelEnabled: false,
    });
    ribGeometry.center();
    const rib = new THREE.Mesh(ribGeometry, sideMaterial);
    rib.position.set(config.widthMm * 0.16, config.lengthMm * 0.2, 0);
    rib.rotation.z = Math.PI / 2;
    rib.castShadow = true;
    group.add(rib);
  }

  return group;
}

export function PlatePreview3D({ config }: { config: ConfiguratorState }) {
  const mountRef = useRef<HTMLDivElement>(null);
  const orbitRef = useRef<OrbitState>(initialOrbit);
  const modelOrientationRef = useRef<ModelOrientation>(initialModelOrientation);
  const targetRef = useRef<ViewTarget>(initialTarget);
  const viewInitializedRef = useRef(false);
  const autoFitDistanceRef = useRef<number | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const rendererRef = useRef<RendererApi | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [orbitState, setOrbitState] = useState(initialOrbit);
  const [modelState, setModelState] = useState(initialModelDisplayState);
  const [controlMode, setControlMode] = useState<'model' | 'camera' | 'pan'>('model');
  const [showBendGuides, setShowBendGuides] = useState(false);
  const [applyBends, setApplyBends] = useState(false);
  const bendLineCount = useMemo(() => getProductBendLines(config).length, [config]);

  useEffect(() => {
    if (bendLineCount > 0) return;
    setShowBendGuides(false);
    setApplyBends(false);
  }, [bendLineCount]);

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};

    async function setup() {
      const THREE = await import('three');
      const mount = mountRef.current;
      if (!mount || cancelled) return;

      const scheduledFrames: number[] = [];
      const scheduledTimers: number[] = [];

      mount.innerHTML = '';
      const scene = new THREE.Scene();

      const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 5000);
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(0xffffff, 0);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.08;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      mount.appendChild(renderer.domElement);

      scene.add(new THREE.HemisphereLight('#ffffff', '#d7d7d7', 2.15));
      const keyLight = new THREE.DirectionalLight('#ffffff', 1.85);
      keyLight.position.set(-260, -340, 420);
      keyLight.castShadow = true;
      scene.add(keyLight);
      const fillLight = new THREE.DirectionalLight('#fff1e8', 0.75);
      fillLight.position.set(300, 250, 250);
      scene.add(fillLight);

      const object = buildSceneObjects(THREE, config, {
        showBendGuides,
        applyBends,
        hideBendGuidesWhenApplied: true,
      });
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      object.position.sub(center);
      const pivot = new THREE.Group();
      pivot.add(object);
      scene.add(pivot);
      const maxDimension = Math.max(size.x, size.y, size.z, 1);
      const cameraTarget = new THREE.Vector3();

      const getViewportSize = () => {
        const width = Math.max(mount.clientWidth || 760, 1);
        const height = Math.max(mount.clientHeight || Math.min(width * 0.58, 560), 260);

        return {
          width,
          height,
          aspect: Math.max(width / height, 0.2),
        };
      };

      const syncPivotOrientation = () => {
        const orientation = modelOrientationRef.current;
        pivot.quaternion
          .set(orientation.x, orientation.y, orientation.z, orientation.w)
          .normalize();
        pivot.updateMatrixWorld(true);
      };

      const getFitDistance = (state = orbitRef.current) => {
        syncPivotOrientation();

        const { aspect } = getViewportSize();
        const provisionalDistance = Math.max(maxDimension * 4, 1000);
        const target = targetRef.current;
        const x = Math.sin(state.yaw) * Math.cos(state.pitch) * provisionalDistance;
        const y = Math.cos(state.yaw) * Math.cos(state.pitch) * provisionalDistance;
        const z = Math.sin(state.pitch) * provisionalDistance;
        camera.position.set(target.x + x, target.y + y, target.z + z);
        camera.lookAt(target.x, target.y, target.z);
        camera.updateMatrixWorld();

        const fitBox = new THREE.Box3().setFromObject(pivot);
        if (fitBox.isEmpty()) return Math.max(maxDimension * 2.4, 640);

        const { min, max } = fitBox;
        const corners = [
          new THREE.Vector3(min.x, min.y, min.z),
          new THREE.Vector3(min.x, min.y, max.z),
          new THREE.Vector3(min.x, max.y, min.z),
          new THREE.Vector3(min.x, max.y, max.z),
          new THREE.Vector3(max.x, min.y, min.z),
          new THREE.Vector3(max.x, min.y, max.z),
          new THREE.Vector3(max.x, max.y, min.z),
          new THREE.Vector3(max.x, max.y, max.z),
        ].map(corner => corner.applyMatrix4(camera.matrixWorldInverse));

        const tanVertical = Math.tan((camera.fov * Math.PI) / 360);
        const tanHorizontal = Math.max(tanVertical * aspect, 0.01);
        const padding = 1.56;
        let requiredDistance = maxDimension * 1.15;

        corners.forEach(corner => {
          const depthOffset = corner.z + provisionalDistance;
          requiredDistance = Math.max(
            requiredDistance,
            depthOffset + (Math.abs(corner.y) * padding) / Math.max(tanVertical, 0.01),
            depthOffset + (Math.abs(corner.x) * padding) / tanHorizontal,
          );
        });

        return Math.max(requiredDistance, maxDimension * 1.2);
      };
      const getMinDistance = (state = orbitRef.current) => {
        void state;
        return Math.max(maxDimension * 0.18, 80);
      };
      const getMaxDistance = (state = orbitRef.current) => {
        void state;
        return Math.max(maxDimension * 24, 2200);
      };
      const fitOrbit = (state: OrbitState) => {
        const fitDistance = clamp(
          getFitDistance(state),
          getMinDistance(state),
          getMaxDistance(state),
        );
        autoFitDistanceRef.current = fitDistance;
        return {
          ...state,
          distance: fitDistance,
        };
      };
      const updateCamera = () => {
        const state = orbitRef.current;
        const target = targetRef.current;
        const distance = clamp(state.distance, getMinDistance(state), getMaxDistance(state));
        const x = Math.sin(state.yaw) * Math.cos(state.pitch) * distance;
        const y = Math.cos(state.yaw) * Math.cos(state.pitch) * distance;
        const z = Math.sin(state.pitch) * distance;
        cameraTarget.set(target.x, target.y, target.z);
        camera.position.set(target.x + x, target.y + y, target.z + z);
        camera.lookAt(cameraTarget);
        camera.updateMatrixWorld();
      };
      const setModelDisplayFromQuaternion = (quaternion: import('three').Quaternion) => {
        const euler = new THREE.Euler().setFromQuaternion(quaternion, 'YXZ');
        setModelState({
          yawDeg: Math.round((euler.y * 180) / Math.PI),
          pitchDeg: Math.round((euler.x * 180) / Math.PI),
        });
      };
      const updateObjectRotation = () => {
        const state = modelOrientationRef.current;
        pivot.quaternion.set(state.x, state.y, state.z, state.w).normalize();
        pivot.updateMatrixWorld(true);
        setModelDisplayFromQuaternion(pivot.quaternion);
      };
      const setObjectOrientation = (orientation: import('three').Quaternion) => {
        orientation.normalize();
        modelOrientationRef.current = {
          x: orientation.x,
          y: orientation.y,
          z: orientation.z,
          w: orientation.w,
        };
        pivot.quaternion.copy(orientation);
        pivot.updateMatrixWorld(true);
        setModelDisplayFromQuaternion(orientation);
      };
      const renderScene = () => renderer.render(scene, camera);
      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      const setPointerFromClient = (clientX: number, clientY: number) => {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(pointer, camera);
      };
      const pickObject = (clientX: number, clientY: number): PickResult => {
        setPointerFromClient(clientX, clientY);
        const intersection = raycaster.intersectObject(pivot, true)[0];
        if (!intersection) return { hit: false };

        return {
          hit: true,
          point: {
            x: intersection.point.x,
            y: intersection.point.y,
            z: intersection.point.z,
          },
        };
      };
      const resize = () => {
        const { width, height, aspect } = getViewportSize();
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(width, height);
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
        if (autoFitDistanceRef.current !== null) {
          orbitRef.current = fitOrbit(orbitRef.current);
          setOrbitState(orbitRef.current);
        }
        updateCamera();
        renderScene();
      };

      const preservedOrbit = viewInitializedRef.current ? orbitRef.current : initialOrbit;
      const shouldUseAutoFit = !viewInitializedRef.current || autoFitDistanceRef.current !== null;
      orbitRef.current = shouldUseAutoFit
        ? fitOrbit(preservedOrbit)
        : {
            ...preservedOrbit,
            distance: clamp(
              preservedOrbit.distance,
              getMinDistance(preservedOrbit),
              getMaxDistance(preservedOrbit),
            ),
          };
      if (!viewInitializedRef.current) {
        modelOrientationRef.current = initialModelOrientation;
        targetRef.current = initialTarget;
      }
      viewInitializedRef.current = true;
      setOrbitState(orbitRef.current);
      updateCamera();
      updateObjectRotation();
      resize();
      const schedulePostLayoutResize = () => {
        const run = () => {
          if (!cancelled && mount.isConnected) resize();
        };
        scheduledFrames.push(
          window.requestAnimationFrame(() => {
            run();
            scheduledFrames.push(window.requestAnimationFrame(run));
          }),
        );
        scheduledTimers.push(window.setTimeout(run, 120), window.setTimeout(run, 360));
      };

      schedulePostLayoutResize();
      setIsReady(true);

      const observer = new ResizeObserver(resize);
      observer.observe(mount);
      const handleWheel = (event: WheelEvent) => {
        event.preventDefault();
        event.stopPropagation();
        rendererRef.current?.zoom(event.deltaY > 0 ? 1.08 : 0.92);
      };
      mount.addEventListener('wheel', handleWheel, { passive: false });

      rendererRef.current = {
        dispose: () => {
          scheduledFrames.forEach(frame => window.cancelAnimationFrame(frame));
          scheduledTimers.forEach(timer => window.clearTimeout(timer));
          mount.removeEventListener('wheel', handleWheel);
          observer.disconnect();
          frontObjectDispose(pivot);
          renderer.dispose();
          renderer.domElement.remove();
        },
        zoom: factor => {
          autoFitDistanceRef.current = null;
          orbitRef.current = {
            ...orbitRef.current,
            distance: clamp(
              orbitRef.current.distance * factor,
              getMinDistance(orbitRef.current),
              getMaxDistance(orbitRef.current),
            ),
          };
          setOrbitState(orbitRef.current);
          updateCamera();
          renderScene();
        },
        reset: () => {
          modelOrientationRef.current = initialModelOrientation;
          targetRef.current = initialTarget;
          orbitRef.current = fitOrbit(initialOrbit);
          setOrbitState(orbitRef.current);
          setControlMode('model');
          updateObjectRotation();
          updateCamera();
          renderScene();
        },
        rotateModelByPointer: (fromX, fromY, toX, toY) => {
          autoFitDistanceRef.current = null;
          if (Math.abs(toX - fromX) < 0.01 && Math.abs(toY - fromY) < 0.01) return;

          const projectToTrackball = (clientX: number, clientY: number) => {
            const rect = renderer.domElement.getBoundingClientRect();
            const radius = Math.max(Math.min(rect.width, rect.height) * 0.5, 1);
            const x = (clientX - rect.left - rect.width * 0.5) / radius;
            const y = (rect.height * 0.5 - (clientY - rect.top)) / radius;
            const distance = Math.hypot(x, y);
            const z = distance < Math.SQRT1_2 ? Math.sqrt(1 - distance * distance) : 0.5 / distance;

            return new THREE.Vector3(x, y, z).normalize();
          };

          const from = projectToTrackball(fromX, fromY);
          const to = projectToTrackball(toX, toY);
          const axis = new THREE.Vector3().crossVectors(from, to);
          if (axis.lengthSq() < 0.000001) return;

          const angle = clamp(
            Math.atan2(axis.length(), clamp(from.dot(to), -1, 1)) *
              MODEL_ROTATION_TRACKBALL_SENSITIVITY,
            -MODEL_ROTATION_MAX_STEP,
            MODEL_ROTATION_MAX_STEP,
          );
          axis.normalize().transformDirection(camera.matrixWorld);

          const current = pivot.quaternion.clone();
          const rotation = new THREE.Quaternion().setFromAxisAngle(axis, angle);
          const next = rotation.multiply(current).normalize();

          setObjectOrientation(next);
          renderScene();
        },
        setPreset: preset => {
          const presets: Record<ViewPreset, import('three').Quaternion> = {
            front: new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI, 0, 0)),
            vertical: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI / 2, 0)),
            horizontal: new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0)),
          };

          modelOrientationRef.current = {
            x: presets[preset].x,
            y: presets[preset].y,
            z: presets[preset].z,
            w: presets[preset].w,
          };
          targetRef.current = initialTarget;
          const presetOrbit =
            preset === 'front' ? { yaw: 0, pitch: 1.35, distance: 1 } : initialOrbit;
          orbitRef.current = fitOrbit(presetOrbit);
          setOrbitState(orbitRef.current);
          setControlMode('model');
          updateObjectRotation();
          updateCamera();
          renderScene();
        },
        orbitCamera: (yaw, pitch) => {
          autoFitDistanceRef.current = null;
          orbitRef.current = {
            ...orbitRef.current,
            yaw,
            pitch,
          };
          setOrbitState(orbitRef.current);
          updateCamera();
          renderScene();
        },
        panView: (deltaX, deltaY, startTarget) => {
          autoFitDistanceRef.current = null;
          const worldPerPixel =
            (2 * Math.tan((camera.fov * Math.PI) / 360) * orbitRef.current.distance) /
            Math.max(renderer.domElement.clientHeight, 1);
          const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
          const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
          const next = new THREE.Vector3(startTarget.x, startTarget.y, startTarget.z)
            .addScaledVector(right, -deltaX * worldPerPixel)
            .addScaledVector(up, deltaY * worldPerPixel);

          targetRef.current = {
            x: next.x,
            y: next.y,
            z: next.z,
          };
          updateCamera();
          renderScene();
        },
        pickObject,
      };
      cleanup = rendererRef.current.dispose;
    }

    setup();

    return () => {
      cancelled = true;
      cleanup();
      rendererRef.current = null;
      setIsReady(false);
    };
  }, [config, showBendGuides, applyBends]);

  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const pick = rendererRef.current?.pickObject(event.clientX, event.clientY) ?? { hit: false };
    const isOrbitGesture = event.button === 1 || event.button === 2 || event.altKey;
    const mode = isOrbitGesture ? 'camera' : pick.hit ? 'model' : 'pan';
    setControlMode(mode);
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      mode,
      orbitYaw: orbitRef.current.yaw,
      orbitPitch: orbitRef.current.pitch,
      target: { ...targetRef.current },
    };
  };

  const drag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const deltaX = event.clientX - dragRef.current.x;
    const deltaY = event.clientY - dragRef.current.y;

    if (dragRef.current.mode === 'model') {
      rendererRef.current?.rotateModelByPointer(
        dragRef.current.x,
        dragRef.current.y,
        event.clientX,
        event.clientY,
      );
      dragRef.current.x = event.clientX;
      dragRef.current.y = event.clientY;
      return;
    }

    if (dragRef.current.mode === 'pan') {
      rendererRef.current?.panView(deltaX, deltaY, dragRef.current.target);
      return;
    }

    const yaw = dragRef.current.orbitYaw - deltaX / CAMERA_ORBIT_HORIZONTAL_SENSITIVITY;
    const pitch = clamp(
      dragRef.current.orbitPitch + deltaY / CAMERA_ORBIT_VERTICAL_SENSITIVITY,
      -1.35,
      1.35,
    );
    rendererRef.current?.orbitCamera(yaw, pitch);
  };

  const stopDrag = () => {
    dragRef.current = null;
  };

  return (
    <div
      className="relative h-full min-h-[260px] overflow-hidden bg-white"
      style={{
        backgroundImage: 'radial-gradient(circle, #e9e9e9 1px, transparent 1px)',
        backgroundPosition: '12px 12px',
        backgroundSize: '24px 24px',
      }}
    >
      <div
        ref={mountRef}
        className="h-full min-h-[260px] w-full cursor-grab touch-none active:cursor-grabbing"
        onPointerDown={startDrag}
        onPointerMove={drag}
        onPointerUp={stopDrag}
        onPointerLeave={stopDrag}
        onContextMenu={event => event.preventDefault()}
      />
      {!isReady && (
        <div className="absolute inset-0 grid place-items-center bg-white/95 text-sm font-semibold text-[#555]">
          Pripravujem 3D model...
        </div>
      )}
      <div className="absolute left-4 top-4 z-20 flex flex-col items-start gap-2">
        {bendLineCount > 0 && (
          <>
            <button
              type="button"
              className={`rounded-full border px-3 py-2 text-xs font-semibold shadow-sm transition ${
                showBendGuides
                  ? 'border-[#2563eb] bg-[#eff6ff] text-[#1d4ed8]'
                  : 'border-[#d9d9d9] bg-white/90 text-[#555] hover:border-black'
              }`}
              onClick={() => setShowBendGuides(value => !value)}
              aria-pressed={showBendGuides}
            >
              Zobraziť ohyby
            </button>
            <button
              type="button"
              className={`rounded-full border px-3 py-2 text-xs font-semibold shadow-sm transition ${
                applyBends
                  ? 'border-[#e63208] bg-[#fff1ec] text-[#c22a05]'
                  : 'border-[#d9d9d9] bg-white/90 text-[#555] hover:border-black'
              }`}
              onClick={() =>
                setApplyBends(value => {
                  const nextValue = !value;
                  if (nextValue) setShowBendGuides(true);
                  return nextValue;
                })
              }
              aria-pressed={applyBends}
            >
              Aplikovať ohyby
            </button>
          </>
        )}
      </div>
      <div data-configurator-view-presets className="absolute bottom-24 right-4 flex gap-2">
        {[
          { label: 'Pohľad spredu', preset: 'front' as const },
          { label: 'Vertikálny pohľad', preset: 'vertical' as const },
          { label: 'Horizontálny pohľad', preset: 'horizontal' as const },
        ].map(item => (
          <button
            key={item.preset}
            type="button"
            className="flex h-12 w-14 items-center justify-center rounded-xl border border-[#d9d9d9] bg-white shadow-sm transition hover:border-black hover:bg-[#fafafa]"
            onClick={() => rendererRef.current?.setPreset(item.preset)}
            aria-label={item.label}
            title={item.label}
          >
            <PlatePresetThumbnail config={config} preset={item.preset} />
          </button>
        ))}
      </div>
      <div className="hidden">
        {controlMode === 'model'
          ? 'rotácia dielu'
          : controlMode === 'pan'
            ? 'posun náhľadu'
            : 'orbit kamery'}{' '}
        · diel {modelState.yawDeg}° / {modelState.pitchDeg}° · kamera{' '}
        {Math.round((orbitState.yaw * 180) / Math.PI)}°
      </div>
    </div>
  );
}

function frontObjectDispose(object: import('three').Object3D) {
  object.traverse(child => {
    const mesh = child as import('three').Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
    const material = mesh.material;
    if (Array.isArray(material)) {
      material.forEach(item => item.dispose());
    } else if (material) {
      material.dispose();
    }
  });
}
